<template>
    <div>
        <v-container fluid pa-0>
            <v-container fluid grid-list-xl class="toolbar-container">
                <v-layout row wrap :class="$bp.smallScreen ? 'mx-0' : null">
                    <v-toolbar color="grey lighten-3" elevation="1">
                        <bc-btn-back></bc-btn-back>
                        <v-toolbar-title class="ml-0">{{ $t($route.meta.title) }}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <bc-modal-print
                            ref="print"
                            @close-method="(file_name = null), $refs.print.close()"
                            :file-name="file_name"
                            :no-button="true"
                        ></bc-modal-print>
                        <bc-btn-refresh v-if="currency.currency_id && currency.currency_id > 0" @invoke-method="dbGetCurrency(true)"></bc-btn-refresh>
                        <v-menu left content-class="no-stripe" v-if="currency.currency_id && currency.currency_id > 0">
                            <template slot="activator">
                                <v-btn icon>
                                    <v-icon>more_vert</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-tile @click="printPDF()">
                                    <v-list-tile-title> Print </v-list-tile-title>
                                </v-list-tile>
                                <template v-if="hasPerm('currencies.update')">
                                    <v-divider class="ml-3 my-0"></v-divider>

                                    <div v-for="item in currency_status_options" :key="item.option_id">
                                        <v-list-tile @click="changeStatus(item)" v-if="item.option_id != currency.status_id">
                                            <v-list-tile-title>
                                                {{ $t(`lang.mark_as_${item.slug}`) ? $t(`lang.mark_as_${item.slug}`) : `Mark as ${item.option}` }}
                                            </v-list-tile-title>
                                        </v-list-tile>
                                    </div>
                                </template>
                                <template v-if="hasPerm('currencies.delete')">
                                    <v-divider class="ml-3 my-0"></v-divider>
                                    <v-list-tile @click="$refs.delete_currency.openDialog()">
                                        <v-list-tile-title> Delete </v-list-tile-title>
                                    </v-list-tile>
                                </template>
                            </v-list>
                        </v-menu>
                        <bc-tooltip>
                            <template slot="activator">
                                <v-btn icon @mouseup.left="$router.push({ name: 'currencies' })">
                                    <v-icon>close</v-icon>
                                </v-btn>
                            </template>
                            {{ $t("lang.back_to_list") }}
                        </bc-tooltip>
                    </v-toolbar>
                </v-layout>
            </v-container>

            <v-card-text>
                <v-card elevation="10">
                    <v-card-text>
                        <div class="ribbon ribbon-top-right" :class="`ribbon-${currency.status_class}`">
                            <span class="ribbon-span">{{ currency.status }}</span>
                        </div>
                        <v-form ref="formCurrency">
                            <v-flex xs12>
                                <v-text-field
                                    v-focus="currency_dialog"
                                    v-model="currency.currency_id"
                                    :label="$t('lang.currency_id')"
                                    :counter="showCounter(currency.currency_id, 'currencies.currency_id')"
                                    :rules="ifValid('currencies.currency_id')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="currency.code"
                                    :label="`${$t('lang.code')} *`"
                                    :counter="showCounter(currency.code, 'currencies.code')"
                                    :rules="ifValid('currencies.code')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="currency.name"
                                    :label="`${$t('lang.name')} *`"
                                    :counter="showCounter(currency.name, 'currencies.name')"
                                    :rules="ifValid('currencies.name')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="currency.symbol"
                                    :label="`${$t('lang.symbol')} *`"
                                    :counter="showCounter(currency.symbol, 'currencies.symbol')"
                                    :rules="ifValid('currencies.symbol')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="currency.exchange_rate"
                                    :label="`${$t('lang.exchange_rate')} *`"
                                    :counter="showCounter(currency.exchange_rate, 'currencies.exchange_rate')"
                                    :rules="ifValid('currencies.exchange_rate')"
                                ></v-text-field>
                            </v-flex>

                            <div>
                                <h3>Currency Details</h3>
                                <v-data-table
                                    :headers="currency_headers"
                                    :items="currency.currency_details || []"
                                    item-key="currency_detail_id"
                                    :no-data-text="$t('lang.no_results_matching')"
                                    hide-actions
                                >
                                    <template slot="items" slot-scope="props">
                                        <tr>
                                            <td>{{ props.item.currency_id }}</td>
                                            <td>{{ props.item.code }}</td>
                                            <td>{{ props.item.name }}</td>
                                            <td>{{ props.item.symbol }}</td>
                                            <td>{{ props.item.exchange_rate }}</td>
                                            <td>
                                                <v-icon @click="removeRow(props.index)">delete</v-icon>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                                <div class="text-center">
                                    <v-btn tile color="primary" @click="addRow()">
                                        {{ $t("lang.add_more") }}
                                    </v-btn>
                                </div>
                            </div>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn @mouseup.left="$router.push({ name: 'currencies' })" flat>{{ $t("lang.cancel") }}</v-btn>
                        <v-btn color="success" @click="saveCurrencies">{{ $t("lang.save") }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-card-text>

            <delete-confirmation-dialog
                ref="delete_currency"
                headline="Confirm Delete?"
                :heading="$t('lang.delete_currency')"
                :message="$t('lang.delete_currency_confirm')"
                @onConfirm="deleteCurrencies()"
            ></delete-confirmation-dialog>
            <delete-confirmation-dialog
                ref="delete_currency_details"
                :heading="$t('lang.delete_package')"
                @onConfirm="confirmDeleteCurrencyDetails()"
            ></delete-confirmation-dialog>
        </v-container>
    </div>
</template>
<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { store } from "Store/store";

export default {
    data() {
        return {
            mobile_list: [{ title: this.$t("lang.print"), action: "printPDF" }],
            currency_headers: [
                { text: this.$t("lang.currency_id"), sortable: false, width: "20%" },
                { text: this.$t("lang.code"), sortable: false, width: "20%" },
                { text: this.$t("lang.name"), sortable: false, width: "20%" },
                { text: this.$t("lang.symbol"), sortable: false, width: "20%" },
                { text: this.$t("lang.exchange_rate"), sortable: false, width: "20%" },
                { text: "", width: "3%", sortable: false },
            ],
            formData: {},
            currency: { currency_details: [] },
            file_name: "",
            dbGetCurrency: _.debounce(this.getCurrency, 1500, { maxWait: 1500, leading: true, trailing: false }),
            customers: null,
        };
    },
    beforeRouteUpdate(to, from, next) {
        const id = `${to.params.id}`;

        if (id !== "undefined") {
            this.$store
                .dispatch("fetchCurrency", {
                    id: id,
                })
                .then((data) => {
                    if (typeof data !== "undefined" && data.currency) {
                        this.currency = this.assignObj2(data.currency, this.rules_currencies);

                        this.$store.dispatch("fetchSetting", {
                            url: "options?slug=currency_status&sort_by=2",
                            method: "get",
                            stateProperty: "currency_status_options",
                            dataProperty: "options",
                            commit: true,
                        });

                        this.finishLoader();
                        next();
                    } else {
                        next({ name: "currencies", replace: true });
                    }
                });
        } else {
            this.finishLoader();
            next();
        }
    },
    beforeRouteEnter(to, from, next) {
        const id = `${to.params.id}`;

        if (id !== "undefined") {
            store
                .dispatch("fetchCurrency", {
                    id: id,
                })
                .then((data) => {
                    if (typeof data !== "undefined" && data.currency) {
                        next((vm) => {
                            vm.currency = vm.assignObj2(data.currency, vm.rules_currencies);

                            store.dispatch("fetchSetting", {
                                url: "options?slug=currency_status&sort_by=2",
                                method: "get",
                                stateProperty: "currency_status_options",
                                dataProperty: "options",
                                commit: true,
                            });

                            vm.finishLoader();
                        });
                    } else {
                        next({ name: "currencies", replace: true });
                    }
                });
        } else {
            next((vm) => {
                vm.finishLoader();
            });
        }

        /*store.dispatch("fetchSetting", {
            url: "options?slug=currency_status&sort_by=2",
            method: "get",
            stateProperty: "currency_status_options",
            dataProperty: "options",
            commit: true,
        });*/
    },
    computed: {
        ...mapState({
            rules_currencies: (state) => state.currencies.rules_currencies,
            currency_status_options: (state) => state.settings.currency_status_options,
            currencies: (state) => state.currencies.currencies,
            selectedCurrency: (state) => state.currencies.selectedCurrency,
        }),
    },
    methods: {
        ...mapActions(["fetchCurrency", "saveCurrency", "deleteCurrency", "printCurrency"]),
        ...mapMutations(["validationOn", "validationOff", "toggleGlobalSnackbar", "toggleModalLoader"]),
        addRow() {
            if (!this.currency.currency_details) this.currency.currency_details = [];
            this.currency.currency_details.push({ currency_id: `${this.$route.params.id}`, quantity: "", sales_unit: "", model_number: "", name: "" });
        },
        removeRow: function (index) {
            this.currency.currency_details.splice(index, 1);
        },
        getCurrency(pg_refresh = false) {
            this.startLoader();

            this.fetchCurrency({
                id: `${this.$route.params.id}`,
            }).then((data) => {
                if (typeof data !== "undefined" && data.currency) {
                    this.currency = this.assignObj2(data.currency, this.rules_currencies);
                    this.toggleModalLoader(false);
                    this.validationOff();
                    this.finishLoader(pg_refresh);
                }
            });
        },
        printPDF() {
            this.dev_mode ? this.$refs.print.openDialog() : this.toggleGlobalSnackbar(true);

            this.printCurrency({
                id: `${this.$route.params.id}`,
            }).then((data) => {
                if (typeof data != "undefined" && data.currency) {
                    if (this.dev_mode) {
                        this.file_name = data.currency.file_name;
                    } else {
                        this.$refs.print.printReport(data.currency.file_name);
                    }

                    if (!this.dev_mode) this.toggleGlobalSnackbar(false);
                } else {
                    this.file_name = "";
                    this.$refs.print.close();
                    this.createAlert();
                }
            });
        },
        saveCurrencies() {
            this.validationOn();
            this.$nextTick(() => {
                if (this.validateForm("formCurrency")) {
                    let body = this.prepForm(this.currency, this.rules_currencies);

                    this.saveCurrency({
                        body: body,
                    }).then((data) => {
                        if (typeof data != "undefined" && data.currency) {
                            this.$router.push({ name: "currency", params: { id: data.currency.currency_id } });
                        } else {
                        }
                    });
                }
            });
        },
        changeStatus(item) {
            this.status_loader = true;
            let body = { currency_id: `${this.$route.params.id}`, status_id: item.option_id };

            this.saveCurrencies({
                body: body,
            }).then((data) => {
                if (typeof data != "undefined" && data.currency) {
                    this.currency = this.assignObj2(data.currency, this.rules_currencies);
                }
            });
        },
        deleteCurrencies() {
            this.deleteCurrency({
                id: `${this.$route.params.id}`,
            }).then((data) => {
                if (data && data.code == "200") {
                    this.$refs.delete_currency_details.close();
                    this.validationOff();
                    this.$router.push({ name: "currencies" });
                }
                this.toggleModalLoader(false);
            });
        },
        getCustomers() {
            this.$store
                .dispatch("fetchCustomers", {
                    url: "me/companies?module=people",
                    method: "get",
                })
                .then((data) => {
                    if (typeof data !== "undefined") {
                        this.customers = data.companies;
                    }
                });
        },
    },
    created() {
        this.getCustomers();
    },
};
</script>
